// Products API service

import type {
  ProductListResponse,
  ProductResponse,
  ProductCategoriesResponse,
  GetProductsQuery,
  CreateProductRequest,
  UpdateProductRequest,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * 取得商品列表
 */
export async function getProducts(
  params?: GetProductsQuery
): Promise<ProductListResponse> {
  const queryParams = new URLSearchParams();

  if (params?.category) queryParams.append('category', params.category);
  if (params?.categories && params.categories.length > 0) {
    params.categories.forEach((cat) => queryParams.append('categories', cat));
  }
  if (params?.search) queryParams.append('search', params.search);
  if (params?.featured !== undefined) queryParams.append('featured', String(params.featured));
  if (params?.status) queryParams.append('status', params.status);
  if (params?.minPrice !== undefined) queryParams.append('minPrice', String(params.minPrice));
  if (params?.maxPrice !== undefined) queryParams.append('maxPrice', String(params.maxPrice));
  if (params?.inStock !== undefined) queryParams.append('inStock', String(params.inStock));
  if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
  if (params?.limit) queryParams.append('limit', String(params.limit));
  if (params?.offset) queryParams.append('offset', String(params.offset));

  const url = `${API_BASE_URL}/api/products${queryParams.toString() ? `?${queryParams}` : ''}`;

  const response = await fetch(url);
  return response.json();
}

/**
 * 取得單一商品
 */
export async function getProductById(id: string): Promise<ProductResponse> {
  const response = await fetch(`${API_BASE_URL}/api/products/${id}`);
  return response.json();
}

/**
 * 取得商品分類
 */
export async function getProductCategories(): Promise<ProductCategoriesResponse> {
  const response = await fetch(`${API_BASE_URL}/api/products/categories`);
  return response.json();
}

/**
 * 建立商品 (需要認證)
 */
export async function createProduct(
  data: CreateProductRequest
): Promise<ProductResponse> {
  const token = localStorage.getItem('auth_token');

  const response = await fetch(`${API_BASE_URL}/api/products`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || '建立商品失敗');
  }

  return result;
}

/**
 * 更新商品 (需要認證)
 */
export async function updateProduct(
  id: string,
  data: UpdateProductRequest
): Promise<ProductResponse> {
  const token = localStorage.getItem('auth_token');

  const response = await fetch(`${API_BASE_URL}/api/products/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || '更新商品失敗');
  }

  return result;
}

/**
 * 刪除商品 (需要認證)
 */
export async function deleteProduct(id: string): Promise<{ success: boolean; data: { message: string } }> {
  const token = localStorage.getItem('auth_token');

  const response = await fetch(`${API_BASE_URL}/api/products/${id}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || '刪除商品失敗');
  }

  return result;
}

/**
 * 批量操作商品 (需要認證)
 */
export interface BatchProductsRequest {
  action: 'publish' | 'unpublish' | 'delete';
  productIds: string[];
}

export interface BatchProductsResponse {
  success: boolean;
  data: {
    processed: number;
    failed: number;
    errors: Array<{ productId: string; error: string }>;
  };
}

export async function batchProducts(
  request: BatchProductsRequest
): Promise<BatchProductsResponse> {
  const token = localStorage.getItem('auth_token');

  const response = await fetch(`${API_BASE_URL}/api/admin/products/batch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error('Batch operation failed');
  }

  return response.json();
}
